/**
 * THE ONE SPELLING OF A USERNAME.
 *
 * A username is unique per tenant, compared case-insensitively. Stored form is
 * always lower case, `a-z 0-9 . _ -`, 3–30 chars, starting and ending with a
 * letter or digit, with no two separators in a row. Login stays by e-mail —
 * the username is a public handle, not a credential.
 */
export const USERNAME_MIN_LENGTH = 3;
export const USERNAME_MAX_LENGTH = 30;

export const USERNAME_PATTERN = /^[a-z0-9]+(?:[._-][a-z0-9]+)*$/;

export const normalizeUsername = (raw: string) => raw.trim().toLowerCase();

export const isValidUsername = (value: string) =>
  value.length >= USERNAME_MIN_LENGTH &&
  value.length <= USERNAME_MAX_LENGTH &&
  USERNAME_PATTERN.test(value);

const clip = (value: string) =>
  value.slice(0, USERNAME_MAX_LENGTH).replace(/[._-]+$/, '');

/**
 * "Marko Petrović" -> "marko.petrovic". Serbian Latin and Cyrillic are
 * transliterated; anything else non-ASCII is dropped. Falls back to "user"
 * when nothing usable is left, and is padded to the minimum length.
 */
const CYRILLIC: Record<string, string> = {
  а: 'a',
  б: 'b',
  в: 'v',
  г: 'g',
  д: 'd',
  ђ: 'dj',
  е: 'e',
  ж: 'z',
  з: 'z',
  и: 'i',
  ј: 'j',
  к: 'k',
  л: 'l',
  љ: 'lj',
  м: 'm',
  н: 'n',
  њ: 'nj',
  о: 'o',
  п: 'p',
  р: 'r',
  с: 's',
  т: 't',
  ћ: 'c',
  у: 'u',
  ф: 'f',
  х: 'h',
  ц: 'c',
  ч: 'c',
  џ: 'dz',
  ш: 's',
};

export const usernameFromName = (name: string) => {
  const ascii = name
    .toLowerCase()
    .replace(/đ/g, 'dj')
    .replace(/[Ѐ-ӿ]/g, (c) => CYRILLIC[c] ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
  const base = clip(
    ascii.replace(/[^a-z0-9]+/g, '.').replace(/^\.+|\.+$/g, ''),
  );
  if (!base) return 'user';
  return base.length < USERNAME_MIN_LENGTH
    ? base.padEnd(USERNAME_MIN_LENGTH, '0')
    : base;
};

/**
 * Candidates to try, best first, for a base such as "marko.petrovic":
 * the base itself, "marko_petrovic", "marko-petrovic2", then numbered
 * variants of the dotted form. Every one is valid by construction.
 */
export const usernameCandidates = (base: string, count = 20) => {
  const out: string[] = [];
  const push = (value: string) => {
    const v = clip(value);
    if (isValidUsername(v) && !out.includes(v)) out.push(v);
  };
  const hasSeparator = /[._-]/.test(base);
  push(base);
  if (hasSeparator) {
    push(base.replace(/[.-]/g, '_'));
    push(`${base.replace(/[._]/g, '-')}2`);
  } else {
    push(`${base}_1`);
    push(`${base}-2`);
  }
  for (let n = 2; out.length < count; n += 1) {
    push(`${base.slice(0, USERNAME_MAX_LENGTH - String(n).length)}${n}`);
  }
  return out;
};
